const VIDEO_EXT = /\.(mp4|webm|mov|m4v|avi|mkv)(\?.*)?$/i;

/**
 * API / admin exports sometimes send gallery fields as JSON strings or newline-separated text.
 */
export function coerceMediaList(raw) {
  if (Array.isArray(raw)) return raw.map((x) => String(x || '').trim()).filter(Boolean);
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.values(raw)
      .map((x) => String(x || '').trim())
      .filter(Boolean);
  }
  if (typeof raw !== 'string') return [];
  const t = raw.trim();
  if (!t) return [];
  if (t.startsWith('[')) {
    try {
      const p = JSON.parse(t);
      return Array.isArray(p) ? p.map((x) => String(x || '').trim()).filter(Boolean) : [];
    } catch {
      /* fall through */
    }
  }
  if (t.includes('\n')) return t.split(/\n/).map((s) => s.trim()).filter(Boolean);
  return [t];
}

function firstStillImageFromProduct(p) {
  if (!p) return '';
  const main = p.image;
  if (main && typeof main === 'string' && !pathLooksLikeVideo(String(main).trim())) {
    return String(main).trim();
  }
  for (const item of coerceMediaList(p.images)) {
    if (!pathLooksLikeVideo(item)) return item;
  }
  return '';
}

/**
 * Root-relative or absolute media path: fix slashes, encode spaces (encodeURI matches browser + Apache well).
 * Ensures leading `/` so /shop/... pages don’t resolve videos against the wrong folder.
 */
export function normalizeMediaUrl(raw) {
  if (raw == null) return '';
  let s = String(raw).trim();
  if (!s) return '';
  if (/^(data:|blob:)/i.test(s)) return s;

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      let p = u.pathname.replace(/\\/g, '/');
      try {
        p = encodeURI(decodeURI(p));
      } catch {
        p = encodeURI(p);
      }
      u.pathname = p;
      return u.toString();
    } catch {
      return s;
    }
  }

  const q = s.indexOf('?');
  let pathPart = (q >= 0 ? s.slice(0, q) : s).replace(/\\/g, '/').trim();
  const query = q >= 0 ? s.slice(q) : '';
  if (pathPart && !pathPart.startsWith('/')) {
    pathPart = `/${pathPart}`;
  }
  let path = pathPart;
  try {
    path = encodeURI(decodeURI(path));
  } catch {
    path = encodeURI(path);
  }
  return path + query;
}

/**
 * Map /data/uploads/<file> to serve_upload.php — CRA dev server and SPA hosts otherwise
 * return index.html for paths that are not part of the static build.
 * Catalog paths stored as /serve_upload.php?f=… are left unchanged (production default).
 */
function rewriteDataUploadsToServePath(pathOnly) {
  if (!pathOnly || pathOnly.includes('..')) return pathOnly;
  const qIdx = pathOnly.indexOf('?');
  const pathPart = qIdx >= 0 ? pathOnly.slice(0, qIdx) : pathOnly;
  const queryRest = qIdx >= 0 ? pathOnly.slice(qIdx) : '';
  const m = /^\/data\/uploads\/([^/]+)$/i.exec(pathPart);
  if (!m) return pathOnly;
  const file = m[1];
  if (!file || file.includes('..') || file.includes('/')) return pathOnly;
  return `/serve_upload.php?f=${encodeURIComponent(file)}${queryRest}`;
}

/**
 * CRA dev server serves files from `public/` before the proxy, so `/serve_upload.php` is often
 * returned as raw PHP text instead of streaming the upload. Point at the real PHP server instead.
 */
function absoluteServeUploadForDev(pathAndQuery) {
  if (typeof process === 'undefined' || !process.env || String(process.env.NODE_ENV) !== 'development') {
    return null;
  }
  const pq = String(pathAndQuery || '');
  if (!pq.startsWith('/')) return null;
  const pathPart = pq.split('?')[0] || '';
  if (!/^\/serve_upload\.php$/i.test(pathPart)) return null;
  const phpOrigin = String(process.env.REACT_APP_PROXY_TARGET || 'http://127.0.0.1:8080').replace(/\/$/, '');
  try {
    return new URL(pq, `${phpOrigin}/`).href;
  } catch {
    return `${phpOrigin}${pq}`;
  }
}

/**
 * Root-relative or absolute href for static files in `public/` (same tab as the React app).
 * Avoids bad `new URL` combinations; works with CRA `PUBLIC_URL` (`''`, `/subdir`, or full site URL).
 */
export function hrefForSameSiteAsset(raw) {
  if (raw == null) return '';
  const rawStr = String(raw).trim();
  if (!rawStr || /^(data:|blob:)/i.test(rawStr)) return rawStr;
  const p = normalizeMediaUrl(rawStr);
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;

  const baseRaw =
    typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL
      ? String(process.env.PUBLIC_URL)
      : '';
  /** CRA + homepage full URL can set PUBLIC_URL to https://… — breaks <img src> on localhost (loads wrong host). */
  const isDev =
    typeof process !== 'undefined' && process.env && String(process.env.NODE_ENV) === 'development';
  const base = (isDev && /^https?:\/\//i.test(baseRaw) ? '' : baseRaw).replace(/\/$/, '');
  let pathOnly = p.startsWith('/') ? p : `/${p}`;
  pathOnly = rewriteDataUploadsToServePath(pathOnly);

  const devUpload = absoluteServeUploadForDev(pathOnly);
  if (devUpload) return devUpload;

  /** Catalog media must stay root-relative in production (CRA homepage URL must not rewrite these). */
  if (
    /^\/serve_upload\.php/i.test(pathOnly.split('?')[0] || '') ||
    /^\/data\/uploads\//i.test(pathOnly)
  ) {
    return pathOnly;
  }

  if (!base || base === '.') return pathOnly;
  if (/^https?:\/\//i.test(base)) {
    try {
      const root = base.endsWith('/') ? base : `${base}/`;
      return new URL(pathOnly.replace(/^\//, ''), root).href;
    } catch {
      return `${base}${pathOnly}`;
    }
  }
  if (base.startsWith('/')) {
    return `${base}${pathOnly}`;
  }
  return `${pathOnly}`;
}

/**
 * Absolute URL for <video>/<img src> in the browser (modals, new-tab links).
 */
export function resolveMediaUrl(raw) {
  if (raw == null) return '';
  const rawStr = String(raw).trim();
  if (!rawStr || /^(data:|blob:)/i.test(rawStr)) return rawStr;
  const href = hrefForSameSiteAsset(rawStr);
  if (!href || /^(data:|blob:)/i.test(href)) return href;
  if (/^https?:\/\//i.test(href)) return href;
  /** Match product grids: root-relative paths stay relative so the dev server / proxy resolve them correctly. */
  if (href.startsWith('/')) return href;
  if (typeof window === 'undefined' || !window.location?.origin) return href;
  try {
    return new URL(href, window.location.origin).href;
  } catch {
    return `${window.location.origin}${href.startsWith('/') ? href : `/${href}`}`;
  }
}

export function pathLooksLikeVideo(s) {
  return typeof s === 'string' && VIDEO_EXT.test(s.trim());
}

/**
 * Ordered gallery paths for grid + lightbox: merges `image` (primary) with `images[]` from API / static data.
 * Avoids missing the cover when MySQL stores primary in `image` and extras in `images_json` → `images`.
 */
export function productGalleryImagePaths(p) {
  if (!p) return [];
  const main = String(p.image || '').trim();
  const arr = Array.isArray(p.images)
    ? p.images.map((x) => String(x || '').trim()).filter(Boolean)
    : coerceMediaList(p.images);
  if (arr.length === 0) return main ? [main] : [];
  if (!main) return [...arr];
  if (arr[0] === main) return [...arr];
  if (arr.includes(main)) return [main, ...arr.filter((u) => u !== main)];
  return [main, ...arr];
}

/** Product has a dedicated video field, extra videos, or image path is a video file. */
export function productHasVideo(p) {
  if (!p) return false;
  if (String(p.video || '').trim()) return true;
  if (coerceMediaList(p.videos).length > 0) return true;
  if (pathLooksLikeVideo(p.image)) return true;
  return false;
}

/** URL opened in VideoModal / <video src> — prefers `video`, then first `videos[]`, then video-like `image`. */
export function primaryVideoSource(p) {
  if (!p) return '';
  const v = String(p.video || '').trim();
  if (v) return normalizeMediaUrl(v);
  const vids = coerceMediaList(p.videos);
  if (vids.length > 0) return normalizeMediaUrl(vids[0]);
  if (pathLooksLikeVideo(p.image)) return normalizeMediaUrl(String(p.image).trim());
  return '';
}

/**
 * Normalized path to a real still image for video tiles, or '' if none (no logo placeholder).
 */
export function videoStillPosterNormalized(p) {
  const still = firstStillImageFromProduct(p);
  return still ? normalizeMediaUrl(still) : '';
}

/**
 * Poster for grid thumbnail when product is treated as video.
 * Uses main image or first non-video gallery file; otherwise optional fallback (empty = no placeholder).
 */
export function videoPosterSrc(p, fallback = '') {
  const fb = String(fallback || '').trim();
  if (!p) return fb ? hrefForSameSiteAsset(fb) : '';
  const still = firstStillImageFromProduct(p);
  if (still) return hrefForSameSiteAsset(still);
  return fb ? hrefForSameSiteAsset(fb) : '';
}
